'use client';

import { useForm } from 'react-hook-form';
import { 
  TextField, 
  Button, 
  Container, 
  Typography, 
  Box,
  Paper,
  Alert,
  Snackbar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import CircularProgress from '@mui/material/CircularProgress';
import Cookies from 'js-cookie';

type FormData = {
  companyCode: string;
};

// Cores da identidade visual
const COLORS = {
  background: '#1A004B', // Roxo escuro
  card: '#FFFFFF',       // Branco
  primary: '#7C5AE0',    // Roxo claro (botão)
  text: '#1A004B',       // Roxo escuro (texto)
  inputBg: '#F5F3FA',    // Fundo dos inputs
  inputBorder: '#E0D7F3' // Borda dos inputs
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: theme.spacing(4),
  borderRadius: '20px',
  background: COLORS.card,
  boxShadow: '0 4px 24px 0 rgba(26,0,75,0.10)',
}));

const StyledContainer = styled(Container)(() => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: COLORS.background,
  padding: '2rem',
  margin: 0,
  maxWidth: '100% !important',
  width: '100%',
}));

const StyledBox = styled(Box)(() => ({
  width: '100%',
  maxWidth: '600px',
  margin: '0 auto',
}));

const StyledButton = styled(Button)(() => ({
  background: COLORS.primary,
  color: '#fff',
  fontWeight: 500,
  borderRadius: '24px',
  textTransform: 'none',
  fontSize: '1rem',
  '&:hover': {
    background: '#6842C2',
  },
}));

const StyledTextField = styled(TextField)(() => ({
  borderRadius: '8px',
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: COLORS.inputBorder,
    },
    '&:hover fieldset': {
      borderColor: COLORS.primary,
    },
    '&.Mui-focused fieldset': {
      borderColor: COLORS.primary,
    },
  },
}));

export default function Home() {
  const router = useRouter();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>();

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');

  const API_CONFIG = {
    baseUrl: process.env.NEXT_PUBLIC_API_URL,
    X_API_KEY: process.env.NEXT_PUBLIC_X_API_KEY,
    endpoints: {
      register: '/companies/validate-login-code'
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_CONFIG.baseUrl}${API_CONFIG.endpoints.register}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json', 
          'X-API-Key': API_CONFIG.X_API_KEY
        },
        body: JSON.stringify({
          login_code: data.companyCode
        })
      });

      const responseData = await response.json();

      if (!response.ok) {
        if (responseData.error === 'Invalid Login Code') {
          setApiError('Código de identificação inválido');
          setLoading(false);
          return;
        }
        setApiError(responseData.error || 'Código da empresa inválido');
        setLoading(false);
        return;
      }

      // setTimeout(() => {
      //   setLoading(false);
      // }, 1000)
      // Set the cookie before redirecting
      Cookies.set('company_code', data.companyCode, { 
        expires: 1, // Expires in 1 day
        secure: process.env.NODE_ENV === 'production',
        sameSite: 'strict'
      });
      router.push(`/employees/register/${responseData.company.uuid}`);
    } catch (error) {
      setApiError(error instanceof Error ? error.message : 'Erro ao validar o código da empresa. Tente novamente.');
      setLoading(false);
    }
  };

  return (
    <StyledContainer>
      <StyledBox>
        <StyledPaper elevation={3}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            {/* Logo no topo */}
            <Image src="/logo-elleve.svg" alt="Logo Elleve Performance" width={120} height={40} style={{ marginBottom: 8 }} />
            {/* Ilustração central */}
            <Image src="/illustration.svg" alt="Ilustração" width={120} height={90} style={{ marginBottom: 8 }} />
          </Box>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ color: '#1A004B', fontWeight: 700 }}>
                Identifique sua empresa
              </Typography>
              <Typography variant="body2" align="center" sx={{ color: '#666', mb: 2 }}>
                Para iniciar o processo de pré-cadastro, preencha o campo abaixo<br />com o código de identificação da sua empresa.
              </Typography>
              <StyledTextField
                {...register('companyCode', {
                  required: 'Código de identificação é obrigatório',
                  minLength: {
                    value: 3,
                    message: 'Código deve ter pelo menos 3 caracteres'
                  }
                })}
                label="Código de identificação*"
                variant="outlined"
                fullWidth
                error={!!errors.companyCode || !!apiError}
                helperText={errors.companyCode?.message || apiError}
                inputProps={{ style: { textTransform: 'uppercase' } }}
                onChange={e => {
                  e.target.value = e.target.value.toUpperCase();
                  register('companyCode').onChange(e);
                  setApiError(''); // Clear API error when user types
                }}
              />
              <StyledButton
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                sx={{ mt: 2 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Próximo'}
              </StyledButton>
            </div>
          </form>
        </StyledPaper>
      </StyledBox>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </StyledContainer>
  );
}
